<template>
  <v-container
    id="returns"
    fluid
    tag="section"
    class="returns-container pa-0 pa-md-3"
  >
    <ReturnRegisterDialog
      v-model="dialog"
      :context="dialog_context"
      @done="on_registered"
    />
    <ReturnLabelDialog
      v-model="label_dialog"
      :context="label_context"
      @created="refresh_expected"
    />

    <div class="returns-content">
      <!-- Plan gate -->
      <v-card v-if="!get_allowed" rounded="lg" elevation="2" class="pa-6 text-center">
        <v-icon size="48" color="primary" class="mb-3">mdi-keyboard-return</v-icon>
        <h2 class="text-h6 font-weight-medium mb-2">Returns management</h2>
        <p class="grey--text text--darken-1 mb-4">
          Scan incoming parcels, register returns against Shopify, restock good items to their shelf and report damaged goods.
          Available on Pro plans and above.
        </p>
        <v-btn color="primary" rounded to="/auth/plans">See plans</v-btn>
      </v-card>

      <template v-else>
        <!-- Store permissions -->
        <v-alert
          v-if="store_needs_permissions"
          type="warning"
          dense
          border="left"
          elevation="2"
          class="mb-4"
        >
          {{ selected_store && selected_store.name }} needs updated Shopify permissions before returns can be registered.
          <router-link to="/auth/dashboard" class="ml-1">Update permissions on the Dashboard</router-link>
        </v-alert>

        <!-- Scan / search -->
        <v-card rounded="lg" elevation="2" class="mb-4 pa-4 scan-card">
          <v-row align="center">
            <v-col class="grow">
              <v-text-field
                ref="searchInput"
                v-model="search_query"
                :label="uses_barcode_scanner ? 'Scan a parcel or search order number, customer or tracking' : 'Search order number, customer, tracking number or scan a label'"
                prepend-icon="mdi-barcode-scan"
                clearable
                outlined
                dense
                hide-details
                autocomplete="off"
                :inputmode="uses_barcode_scanner ? 'none' : 'search'"
                :loading="searching"
                @input="on_search_input"
                @keydown.enter.prevent="submit_search({ from_scan: uses_barcode_scanner })"
                @keydown.tab.prevent="submit_search({ from_scan: true })"
                @click:clear="clear_search"
                class="search-field"
              />
            </v-col>
            <v-col class="shrink d-flex align-center">
              <v-btn
                v-if="!uses_barcode_scanner"
                icon
                large
                :color="camera ? 'primary' : ''"
                title="Scan with camera"
                @click="camera = !camera"
              >
                <v-icon>mdi-camera</v-icon>
              </v-btn>
              <v-select
                v-if="get_return_stores.length > 1"
                :items="get_return_stores"
                :value="selected_data_source_id"
                @change="change_store"
                outlined
                dense
                hide-details
                label="Store"
                item-value="id"
                item-text="name"
                class="store-selector ml-2"
              />
            </v-col>
          </v-row>

          <v-expand-transition>
            <div v-if="camera && !uses_barcode_scanner" class="camera-wrap mt-3">
              <BarcodeScanner :active="camera && !dialog" @decode="on_camera_decode" />
            </div>
          </v-expand-transition>

          <!-- Search results -->
          <v-expand-transition>
            <div v-if="search_query && (results.length || searched)" class="results mt-4">
              <div class="text-caption grey--text mb-2">
                {{ results.length ? `${results.length} match${results.length === 1 ? '' : 'es'} for "${search_query}"` : `Nothing found for "${search_query}"` }}
                <span v-if="!results.length && searched"> · Checked expected returns, shipped parcels and the Shopify order list.</span>
              </div>
              <v-list v-if="results.length" dense class="results-list py-0" rounded>
                <v-list-item
                  v-for="(result, i) in results"
                  :key="`result-${i}`"
                  @click="open_result(result)"
                  class="result-item"
                >
                  <v-list-item-avatar :color="result_meta(result).color" size="36">
                    <v-icon dark small>{{ result_meta(result).icon }}</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ result.order_name }}
                      <span class="grey--text text--darken-1 font-weight-regular ml-1">{{ result.customer_name }}</span>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ result_meta(result).label }}
                      <span v-if="result.tracking_number"> · {{ result.tracking_number }}</span>
                      <span v-if="result.items_summary"> · {{ result.items_summary }}</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-icon>mdi-chevron-right</v-icon>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </div>
          </v-expand-transition>
        </v-card>

        <v-row>
          <!-- Expected returns -->
          <v-col cols="12" md="4">
            <v-card rounded="lg" elevation="2" class="section-card">
              <v-card-title class="section-title">
                <v-icon color="primary" class="mr-2">mdi-truck-delivery-outline</v-icon>
                Expected returns
                <v-spacer></v-spacer>
                <v-btn icon small :loading="loading_expected" @click="refresh_expected" title="Refresh">
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>
              <div v-if="loading_expected && !get_expected.length" class="empty-state">
                <v-progress-circular indeterminate color="primary" size="28" />
              </div>
              <div v-else-if="!get_expected.length" class="empty-state grey--text">
                <v-icon class="mb-2" color="grey lighten-1">mdi-check-circle-outline</v-icon>
                <div>No open returns in Shopify and no parcels reported as coming back.</div>
              </div>
              <v-list v-else dense class="py-0">
                <v-list-item
                  v-for="(expected, i) in get_expected"
                  :key="`expected-${i}`"
                  @click="open_expected(expected)"
                  class="expected-item"
                >
                  <v-list-item-avatar :color="expected.type === 'solicited' ? 'primary' : 'orange'" size="36">
                    <v-icon dark small>{{ expected.type === 'solicited' ? 'mdi-clipboard-check-outline' : 'mdi-package-variant' }}</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ expected.order_name }}
                      <span class="grey--text text--darken-1 font-weight-regular ml-1">{{ expected.customer_name }}</span>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      <template v-if="expected.type === 'solicited'">
                        {{ expected.return_name }} · {{ expected.return_status === 'REQUESTED' ? 'Requested, awaiting approval' : 'Open in Shopify' }}
                      </template>
                      <template v-else>
                        Returned by carrier<span v-if="expected.carrier_description">: {{ expected.carrier_description }}</span>
                      </template>
                      · {{ expected.created_at | DateFilter }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle class="text-caption">
                      {{ items_summary(expected.items) }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-if="expected.type === 'solicited' && expected.tracking_numbers.length" class="text-caption">
                      <v-icon x-small class="mr-1">mdi-tag-outline</v-icon>Return label {{ expected.tracking_numbers.map(t => t.number).join(', ') }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action class="expected-actions">
                    <v-btn
                      v-if="expected.type === 'solicited' && expected.return_status === 'OPEN' && get_return_labels_possible"
                      icon
                      small
                      :title="expected.tracking_numbers.length ? 'Return label' : 'Create return label'"
                      :color="expected.tracking_numbers.length ? 'success' : 'primary'"
                      @click.stop="open_label_dialog(expected)"
                    >
                      <v-icon small>{{ expected.tracking_numbers.length ? 'mdi-tag-check-outline' : 'mdi-tag-plus-outline' }}</v-icon>
                    </v-btn>
                    <v-chip x-small label>{{ expected.remaining_quantity }} unit{{ expected.remaining_quantity === 1 ? '' : 's' }}</v-chip>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>

          <!-- Awaiting inspection -->
          <v-col cols="12" md="4">
            <v-card rounded="lg" elevation="2" class="section-card">
              <v-card-title class="section-title">
                <v-icon color="warning" class="mr-2">mdi-magnify-scan</v-icon>
                Awaiting inspection
              </v-card-title>
              <v-divider></v-divider>
              <div v-if="!get_holds.length" class="empty-state grey--text">
                <v-icon class="mb-2" color="grey lighten-1">mdi-check-circle-outline</v-icon>
                <div>No items on hold.</div>
              </div>
              <v-list v-else dense class="py-0">
                <v-list-item v-for="hold in get_holds" :key="hold.id" class="hold-item">
                  <v-list-item-avatar size="40" rounded class="hold-image">
                    <v-img v-if="hold.image" :src="hold.image" contain />
                    <v-icon v-else>mdi-package-variant-closed</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">{{ hold.quantity }}x {{ hold.name }}</v-list-item-title>
                    <v-list-item-subtitle>
                      {{ hold.return_doc.order_name }} · {{ hold.return_doc.customer_name }} · held {{ hold.return_doc.registered_at | DateFilter }}
                    </v-list-item-subtitle>
                    <v-list-item-subtitle v-if="hold.note" class="font-italic">"{{ hold.note }}"</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action class="hold-actions">
                    <v-btn x-small rounded color="success" dark :loading="resolving === hold.id" @click="resolve_hold(hold, 'good')">Restock</v-btn>
                    <v-btn x-small rounded color="error" dark :loading="resolving === hold.id" @click="resolve_hold(hold, 'damaged')">Damaged</v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>

          <!-- Awaiting store owner: received, still open in Shopify -->
          <v-col cols="12" md="4">
            <v-card rounded="lg" elevation="2" class="section-card">
              <v-card-title class="section-title">
                <v-icon color="blue-grey" class="mr-2">mdi-account-clock-outline</v-icon>
                Awaiting store owner
                <ToolTip>
                  Returns where all goods were received but the return is still open in Shopify. The owner refunds, exchanges or closes it in the Shopify admin, or you close it here without a refund. Change what happens automatically under Settings > Returns. The list refreshes when you come back to this tab.
                </ToolTip>
                <v-spacer></v-spacer>
                <v-btn icon small :loading="loading_expected" @click="refresh_expected" title="Refresh">
                  <v-icon small>mdi-refresh</v-icon>
                </v-btn>
              </v-card-title>
              <v-divider></v-divider>
              <div v-if="!get_awaiting_owner.length" class="empty-state grey--text">
                <v-icon class="mb-2" color="grey lighten-1">mdi-check-circle-outline</v-icon>
                <div>Nothing waiting for the store owner.</div>
              </div>
              <v-list v-else dense class="py-0">
                <v-list-item v-for="row in get_awaiting_owner" :key="row.return_id" class="hold-item">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-medium">
                      {{ row.order_name }}
                      <span class="grey--text text--darken-1 font-weight-regular ml-1">{{ row.customer_name }}</span>
                    </v-list-item-title>
                    <v-list-item-subtitle>{{ row.return_name }} · received, open since {{ row.created_at | DateFilter }}</v-list-item-subtitle>
                    <v-list-item-subtitle class="text-caption">{{ received_summary(row.items) }}</v-list-item-subtitle>
                  </v-list-item-content>
                  <v-list-item-action class="hold-actions">
                    <v-btn x-small rounded outlined color="primary" :href="shopify_order_url(row)" target="_blank" rel="noopener">
                      <v-icon left x-small>mdi-open-in-new</v-icon>Shopify
                    </v-btn>
                    <v-btn x-small rounded color="success" dark :loading="refunding === row.return_id" :disabled="closing === row.return_id" @click="refund_return(row)">Refund</v-btn>
                    <v-btn x-small rounded color="blue-grey" dark :loading="closing === row.return_id" :disabled="refunding === row.return_id" @click="close_return(row)">Close</v-btn>
                  </v-list-item-action>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
        </v-row>

        <!-- Recent returns -->
        <v-card rounded="lg" elevation="2" class="mt-4 section-card">
          <v-card-title class="section-title">
            <v-icon color="primary" class="mr-2">mdi-history</v-icon>
            Recent returns
          </v-card-title>
          <v-divider></v-divider>
          <v-data-table
            :headers="history_headers"
            :items="get_history"
            :loading="loading_history"
            :items-per-page="10"
            item-key="id"
            no-data-text="No returns registered yet"
            class="history-table"
          >
            <template v-slot:[`item.registered_at`]="{ item }">
              {{ item.registered_at | DateFilter }}
            </template>
            <template v-slot:[`item.order_name`]="{ item }">
              <span class="font-weight-medium">{{ item.order_name }}</span>
              <div class="text-caption grey--text">{{ item.customer_name }}</div>
            </template>
            <template v-slot:[`item.items`]="{ item }">
              <div v-for="line in item.items" :key="line.id" class="text-caption history-line">
                <v-icon x-small :color="condition_color(line.condition)" class="mr-1">{{ condition_icon(line.condition) }}</v-icon>
                {{ line.quantity }}x {{ line.name }}
                <span class="grey--text">· {{ condition_label(line) }}</span>
                <v-btn v-if="line.photo_refs && line.photo_refs.length" icon x-small class="ml-1" @click="open_photos(line)" title="Photos">
                  <v-icon x-small>mdi-camera</v-icon>
                </v-btn>
              </div>
            </template>
            <template v-slot:[`item.source`]="{ item }">
              <v-chip x-small label :color="source_meta(item.source).color" text-color="white">{{ source_meta(item.source).label }}</v-chip>
            </template>
            <template v-slot:[`item.status`]="{ item }">
              <v-chip x-small label :color="item.status === 'on_hold' ? 'warning' : 'success'" text-color="white">
                {{ item.status === 'on_hold' ? 'On hold' : 'Completed' }}
              </v-chip>
              <div v-for="line in outcome_lines(item)" :key="line" class="text-caption grey--text text--darken-1 mt-1">{{ line }}</div>
            </template>
            <template v-slot:[`item.shopify_return_names`]="{ item }">
              <a
                v-if="item.shopify_return_names && item.shopify_return_names.length"
                :href="shopify_order_url(item)"
                target="_blank"
                rel="noopener"
              >{{ item.shopify_return_names.join(', ') }}</a>
              <span v-else class="grey--text">-</span>
            </template>
            <template v-slot:[`item.registered_by_name`]="{ item }">
              <UserAvatar v-if="item.registered_by" :user-id="item.registered_by" :size="28" :title="item.registered_by_name" />
              <span v-else>{{ item.registered_by_name }}</span>
            </template>
          </v-data-table>
        </v-card>
      </template>
    </div>

    <!-- Photos dialog -->
    <v-dialog v-model="photo_dialog" max-width="600" :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card rounded="lg">
        <v-card-title>
          <v-icon color="primary" class="mr-2">mdi-camera</v-icon>
          Return photos
          <v-spacer></v-spacer>
          <v-btn icon @click="photo_dialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text>
          <v-carousel v-if="photos.length" height="400" hide-delimiter-background show-arrows-on-hover>
            <v-carousel-item v-for="(photo, i) in photos" :key="i" :src="photo" contain />
          </v-carousel>
          <div v-else class="text-center py-8"><v-progress-circular indeterminate color="primary" /></div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage'
import BarcodeScanner from '@/components/BarcodeScanner'
import UserAvatar from '@/components/UserAvatar'
import ReturnRegisterDialog from '@/components/returns/ReturnRegisterDialog'
import ReturnLabelDialog from '@/components/returns/ReturnLabelDialog'
import ToolTip from '@/components/ToolTip.vue'
import { DateFilter } from '@/utils/filters'
import { normalize_barcode, tracking_candidates } from '@/utils/barcode'

const CONDITION = {
  good: { icon: 'mdi-check', color: 'success', label: 'restocked' },
  damaged: { icon: 'mdi-package-variant-remove', color: 'error', label: 'damaged' },
  hold: { icon: 'mdi-magnify-scan', color: 'warning', label: 'on hold' },
  missing: { icon: 'mdi-help', color: 'grey', label: 'missing' },
}
const SOURCE = {
  solicited: { label: 'Expected', color: 'primary' },
  unsolicited: { label: 'Came back', color: 'orange' },
  manual_restock: { label: 'Restock only', color: 'grey' },
}

export default {
  components: { BarcodeScanner, UserAvatar, ReturnRegisterDialog, ReturnLabelDialog, ToolTip },
  filters: { DateFilter },
  data(){
    return {
      search_query: '',
      search_timeout: null,
      searching: false,
      searched: false,
      results: [],
      camera: false,
      dialog: false,
      dialog_context: {},
      label_dialog: false,
      label_context: {},
      resolving: null,
      closing: null,
      refunding: null,
      last_refresh: 0,
      photo_dialog: false,
      photos: [],
      history_headers: [
        { text: 'Registered', value: 'registered_at', sortable: false },
        { text: 'Order', value: 'order_name', sortable: false },
        { text: 'Items', value: 'items', sortable: false },
        { text: 'Type', value: 'source', sortable: false, align: 'center' },
        { text: 'Status', value: 'status', sortable: false, align: 'center' },
        { text: 'Shopify return', value: 'shopify_return_names', sortable: false },
        { text: 'By', value: 'registered_by_name', sortable: false, align: 'center', width: '70px' },
      ],
    }
  },
  computed: {
    ...mapState({
      company: state => state.warehouse.company,
      selected_data_source_id: state => state.returns.selected_data_source_id,
      loading_expected: state => state.returns.loading_expected,
      loading_history: state => state.returns.loading_history,
      scopes_updated_at: state => state.app.config.shopify_scopes_last_updated_at,
      lists_loaded: state => Array.isArray(state.returns.expected[state.returns.selected_data_source_id]),
    }),
    ...mapGetters({
      get_allowed: 'returns/get_allowed',
      get_expected: 'returns/get_expected',
      get_history: 'returns/get_history',
      get_holds: 'returns/get_holds',
      get_awaiting_owner: 'returns/get_awaiting_owner',
      get_return_labels_possible: 'returns/get_return_labels_possible',
      get_main_store: 'warehouse/get_main_store',
      get_return_stores: 'returns/get_return_stores',
    }),
    uses_barcode_scanner(){
      return this.company.uses_barcode_scanner === true
    },
    selected_store(){
      return this.get_return_stores.find(ds => ds.id === this.selected_data_source_id)
    },
    store_needs_permissions(){
      const store = this.selected_store
      if(!store || !this.scopes_updated_at) return false
      return !store.shopify_scopes_last_updated_at
        || new Date(store.shopify_scopes_last_updated_at).getTime() < new Date(this.scopes_updated_at).getTime()
    },
  },
  methods: {
    items_summary(items){
      return (items || []).map(i => `${i.quantity}x ${i.name}`).join(', ')
    },
    received_summary(items){
      const label = { RESTOCKED: 'restocked', NOT_RESTOCKED: 'damaged', MISSING: 'missing', PROCESSING_REQUIRED: 'inspection' }
      return (items || []).map(i => `${i.quantity}x ${i.name} (${label[i.disposition] || i.disposition})`).join(', ')
    },
    // Where a return stands in Shopify right now, from the lists fetched for this page:
    // 'awaiting' (received, still open), 'expected' (open, units still to come), 'closed', or null when not loaded yet
    live_return_state(return_id){
      if(this.get_awaiting_owner.some(a => a.return_id === return_id)) return 'awaiting'
      if(this.get_expected.some(e => e.return_id === return_id)) return 'expected'
      return this.lists_loaded ? 'closed' : null
    },
    // Why an exchange was or was not moved forward, for the history line
    exchange_note(r){
      if(!r.exchange) return ''
      if(['processed', 'released'].includes(r.exchange)) return ', exchange released'
      if(r.exchange === 'awaiting_payment') return `, exchange awaits payment of ${r.balance_due} ${r.currency || ''}`.trimEnd() + (r.invoice_sent ? ' (request sent)' : ' (request failed)')
      if(r.exchange === 'waiting_for_units') return ', exchange waits for remaining units'
      if(r.exchange === 'needs_owner_condition') return ', exchange not released (not all units in good condition)'
      return `, exchange not released (${String(r.exchange).replace(/^left_for_owner: /, '')})`
    },
    // What happened in Shopify after receipt, per return touched by this registration. What we did
    // (refund, close) is stored on the document; what the owner did later comes from the live state.
    outcome_lines(doc){
      return (doc.after_receipt?.results || []).map(r => {
        const name = r.return_name || 'Return'
        const exchange = this.exchange_note(r)
        if(r.action === 'refunded') return `${name}: refunded ${r.amount} ${r.currency || ''}`.trim() + exchange
        if(r.action === 'processed') return `${name}: processed, nothing to refund` + exchange
        if(r.action === 'closed') return `${name}: closed${exchange ? exchange : ', no refund'}`
        if(r.action === 'already_closed') return `${name}: closed`
        const live = this.live_return_state(r.return_id)
        if(live === 'closed') return `${name}: closed in Shopify`
        if(live === 'expected') return `${name}: open, more units expected`
        if(r.action === 'failed') return `${name}: ${doc.after_receipt.mode} failed, awaiting store owner`
        return `${name}: awaiting store owner`
      })
    },
    async refund_return(row){
      const { ok } = await this.$prompt({
        text: `Refund ${row.return_name} on ${row.order_name}? All received units are refunded to the original payment method with the amount Shopify suggests, the customer is notified, and the return closes.`,
        buttons: [
          { text: 'Refund', value: { ok: true } },
          { text: 'Cancel', value: { ok: false } },
        ],
      })
      if(!ok) return
      this.refunding = row.return_id
      try {
        const refund = await this.$store.dispatch('returns/refund_return', { return_id: row.return_id })
        this.$store.commit('app/SET_SNACK_BAR', { text: refund.amount > 0 ? `${row.return_name} refunded ${refund.amount} ${refund.currency || ''}`.trim() : `${row.return_name} processed, nothing to refund`, type: 'success' })
      } catch (e) {
        this.$store.commit('app/SET_SNACK_BAR', { text: e.message, type: 'error' })
      } finally {
        this.refunding = null
      }
    },
    async close_return(row){
      const { ok } = await this.$prompt({
        text: `Close ${row.return_name} on ${row.order_name} without a refund? Shopify will show the order as returned. Use the Shopify admin instead if the customer should be refunded.`,
        buttons: [
          { text: 'Close without refund', value: { ok: true } },
          { text: 'Cancel', value: { ok: false } },
        ],
      })
      if(!ok) return
      this.closing = row.return_id
      try {
        await this.$store.dispatch('returns/close_return', { return_id: row.return_id })
        this.$store.commit('app/SET_SNACK_BAR', { text: `${row.return_name} closed`, type: 'success' })
      } catch (e) {
        this.$store.commit('app/SET_SNACK_BAR', { text: e.message, type: 'error' })
      } finally {
        this.closing = null
      }
    },
    condition_icon(condition){ return (CONDITION[condition] || CONDITION.missing).icon },
    condition_color(condition){ return (CONDITION[condition] || CONDITION.missing).color },
    condition_label(line){
      const base = (CONDITION[line.condition] || CONDITION.missing).label
      return line.note ? `${base}, "${line.note}"` : base
    },
    source_meta(source){ return SOURCE[source] || SOURCE.unsolicited },
    result_meta(result){
      if(result.type === 'solicited') return { icon: 'mdi-clipboard-check-outline', color: 'primary', label: `Expected return ${result.return_name || ''}` }
      if(result.type === 'unsolicited') return { icon: 'mdi-package-variant', color: 'orange', label: 'Parcel returned by carrier' }
      if(result.type === 'parcel') return { icon: 'mdi-airplane-takeoff', color: 'grey darken-1', label: `Shipped ${DateFilter(result.created_at)}` }
      return { icon: 'mdi-shopping-outline', color: 'blue-grey', label: result.expected_quantity ? `Return open in Shopify (${result.expected_quantity} expected)` : 'Shopify order' }
    },
    shopify_order_url(item){
      const store = this.get_return_stores.find(ds => ds.id === (item.data_source_id || this.selected_data_source_id))
      return store?.base_url ? `${store.base_url}/admin/orders/${item.order_id}` : '#'
    },
    async change_store(id){
      await this.$store.dispatch('returns/select_data_source', id)
      this.$store.commit('fulfillments/SET_STATE', { selected_data_source_id: id })
      this.clear_search()
      await this.load()
    },
    async load(){
      if(!this.selected_data_source_id) return
      await Promise.all([
        this.$store.dispatch('returns/fetch_expected').catch(e => this.$store.commit('app/SET_SNACK_BAR', { text: e.message, type: 'error' })),
        this.$store.dispatch('returns/fetch_history').catch(e => this.$store.commit('app/SET_SNACK_BAR', { text: e.message, type: 'error' })),
      ])
    },
    refresh_expected(){
      this.last_refresh = Date.now()
      this.$store.dispatch('returns/fetch_expected').catch(e => this.$store.commit('app/SET_SNACK_BAR', { text: e.message, type: 'error' }))
    },
    // Owners act in the Shopify admin in another tab, so the Shopify-derived lists go stale while this page sits open.
    // Refetch when the tab is shown again, at most every 15 seconds.
    refresh_on_return(){
      if(document.visibilityState !== 'visible' || this.dialog || this.label_dialog || !this.selected_data_source_id) return
      if(Date.now() - this.last_refresh < 15000) return
      this.refresh_expected()
      this.$store.dispatch('returns/fetch_history').catch(() => null)
    },
    focus_search(){
      this.$nextTick(() => {
        const field = this.$refs.searchInput
        if(field && typeof field.focus === 'function') field.focus()
      })
    },
    on_search_input(){
      if(this.search_timeout) clearTimeout(this.search_timeout)
      this.searched = false
      if(!this.search_query || !this.search_query.trim()) return this.clear_search()
      // Hardware scanners type the whole code within milliseconds and may not send Enter
      this.search_timeout = setTimeout(() => this.submit_search({ from_scan: this.uses_barcode_scanner }), this.uses_barcode_scanner ? 300 : 500)
    },
    clear_search(){
      if(this.search_timeout) clearTimeout(this.search_timeout)
      this.search_query = ''
      this.results = []
      this.searched = false
    },
    on_camera_decode(barcode){
      this.search_query = normalize_barcode(barcode) || barcode
      this.submit_search({ from_scan: true })
    },
    // Matches, in order: expected returns (tracking/order/customer), shipped parcels in Firestore, Shopify order search.
    async submit_search({ from_scan = false } = {}){
      if(this.search_timeout) clearTimeout(this.search_timeout)
      const term = (this.search_query || '').trim()
      if(!term || this.searching) return
      this.searching = true
      try {
        const candidates = tracking_candidates(term)
        const matches_tracking = number => candidates.includes(String(number || '').replace(/[\s-]/g, '').toUpperCase())
        const digits = term.replace(/^#/, '')
        const local = this.get_expected
          .filter(e => e.tracking_numbers.some(t => matches_tracking(t.number))
            || String(e.order_name || '').replace(/^#/, '') === digits
            || (term.length > 2 && String(e.customer_name || '').toLowerCase().includes(term.toLowerCase())))
          .map(e => ({ ...e, tracking_number: e.tracking_numbers[0]?.number || null, items_summary: this.items_summary(e.items) }))

        let results = local
        if(!results.length){
          // The selected store first; a scanned parcel may belong to another store the company fulfills
          let parcels = await this.$store.dispatch('fulfillments/search_parcels', { query: term, data_source_id: this.selected_data_source_id })
          let found_in = null
          for(const store of this.get_return_stores.filter(ds => ds.id !== this.selected_data_source_id)){
            if(parcels && parcels.length) break
            parcels = await this.$store.dispatch('fulfillments/search_parcels', { query: term, data_source_id: store.id })
            if(parcels && parcels.length) found_in = store
          }
          if(found_in){
            await this.change_store(found_in.id)
            this.search_query = term
            this.$store.commit('app/SET_SNACK_BAR', { text: `Found in ${found_in.name}, store switched`, type: 'info' })
          }
          results = (parcels || []).map(p => ({
            type: 'parcel',
            order_id: String(p.order_id),
            order_name: p.name,
            customer_name: p.shipping_address?.name || '',
            tracking_number: p.tracking_number || null,
            fulfillment_doc_id: p.sf_fulfillment_id,
            fulfillment_gid: p.admin_graphql_api_id || (p.id ? `gid://shopify/Fulfillment/${p.id}` : null),
            created_at: p.created_at,
            items_summary: this.items_summary(p.line_items),
          }))
        }
        if(!results.length){
          const orders = await this.$store.dispatch('returns/find_order', { query: term })
          results = orders.filter(o => o.items.length).map(o => ({
            type: 'order',
            order_id: o.order.id,
            order_name: o.order.name,
            customer_name: o.order.customer_name,
            tracking_number: o.return_tracking_numbers.find(t => matches_tracking(t.number))?.number || null,
            expected_quantity: o.expected_quantity,
            items_summary: this.items_summary(o.items.map(i => ({ quantity: i.quantity, name: i.name }))),
          }))
        }
        this.results = results
        this.searched = true
        // A scanned code that identifies exactly one shipment goes straight to registration
        if(from_scan && results.length === 1) this.open_result(results[0])
      } catch (e) {
        this.$store.commit('app/SET_SNACK_BAR', { text: e.message, type: 'error' })
      } finally {
        this.searching = false
        if(this.uses_barcode_scanner && !this.dialog) this.focus_search()
      }
    },
    open_result(result){
      if(result.type === 'solicited' || result.type === 'unsolicited') return this.open_expected(result)
      this.open({
        order_id: result.order_id,
        source: result.type === 'parcel' ? 'unsolicited' : (result.expected_quantity ? 'solicited' : 'unsolicited'),
        tracking_number: result.tracking_number,
        fulfillment_doc_id: result.fulfillment_doc_id || null,
        fulfillment_gid: result.fulfillment_gid || null,
        parcel_name: result.type === 'parcel' ? result.order_name : null,
        preselect: result.type === 'parcel' ? 'all' : 'expected',
      })
    },
    open_expected(expected){
      this.open({
        order_id: expected.order_id,
        source: expected.type,
        tracking_number: expected.tracking_numbers[0]?.number || null,
        fulfillment_doc_id: expected.fulfillment_doc_id || null,
        fulfillment_gid: expected.fulfillment_gid || null,
        parcel_name: expected.type === 'unsolicited' ? expected.order_name : null,
        preselect: expected.type === 'unsolicited' ? 'all' : 'expected',
      })
    },
    open(context){
      this.dialog_context = context
      this.dialog = true
    },
    open_label_dialog(expected){
      this.label_context = { return_id: expected.return_id, return_name: expected.return_name, order_name: expected.order_name }
      this.label_dialog = true
    },
    on_registered(){
      this.clear_search()
      this.refresh_expected()
      if(this.uses_barcode_scanner) this.focus_search()
    },
    async resolve_hold(hold, condition){
      const { ok, input: note } = await this.$prompt({
        text: condition === 'good'
          ? `Restock ${hold.quantity}x ${hold.name} from ${hold.return_doc.order_name}? Shopify inventory goes up and the item goes back on its shelf.`
          : `Report ${hold.quantity}x ${hold.name} from ${hold.return_doc.order_name} as damaged? It will not be restocked.`,
        input: { label: 'Note (optional)', value: hold.note || '' },
        buttons: [
          { text: condition === 'good' ? 'Restock' : 'Mark damaged', value: { ok: true } },
          { text: 'Cancel', value: { ok: false } },
        ],
      })
      if(!ok) return
      this.resolving = hold.id
      try {
        await this.$store.dispatch('returns/resolve_hold', { return_id: hold.return_doc.id, item_id: hold.id, condition, note })
        this.$store.commit('app/SET_SNACK_BAR', { text: `${hold.name} ${condition === 'good' ? 'restocked' : 'marked as damaged'}`, type: 'success' })
      } catch (e) {
        this.$store.commit('app/SET_SNACK_BAR', { text: e.message, type: 'error' })
      } finally {
        this.resolving = null
      }
    },
    async open_photos(line){
      this.photos = []
      this.photo_dialog = true
      try {
        this.photos = await Promise.all(line.photo_refs.map(p => getDownloadURL(storageRef(getStorage(), p.ref))))
      } catch (e) {
        this.$store.commit('app/SET_SNACK_BAR', { text: e.message, type: 'error' })
      }
    },
  },
  async mounted(){
    if(!this.get_allowed) return
    if(!this.selected_data_source_id || !this.get_return_stores.some(ds => ds.id === this.selected_data_source_id)){
      // The company's own store first, connected (3PL) stores only when there is no own store
      const main = this.get_main_store && this.get_return_stores.find(ds => ds.id === this.get_main_store.id)
      const first = main || this.get_return_stores[0]
      if(first) await this.$store.dispatch('returns/select_data_source', first.id)
    }
    if(this.selected_data_source_id) this.$store.commit('fulfillments/SET_STATE', { selected_data_source_id: this.selected_data_source_id })
    this.$store.dispatch('user/get_company_users').catch(() => null)
    await this.load()
    this.last_refresh = Date.now()
    window.addEventListener('focus', this.refresh_on_return)
    document.addEventListener('visibilitychange', this.refresh_on_return)
    if(this.uses_barcode_scanner) this.focus_search()
  },
  beforeDestroy(){
    window.removeEventListener('focus', this.refresh_on_return)
    document.removeEventListener('visibilitychange', this.refresh_on_return)
  },
}
</script>

<style lang="scss" scoped>
.returns-container {
  background-color: #f6f6f7;
  min-height: 100vh;
  padding-bottom: 24px;
}

.returns-content {
  animation: fadeIn 0.6s ease-out;
}

.store-selector {
  min-width: 10em;
}

.camera-wrap {
  max-width: 480px;
  margin: 0 auto;
}

.results-list {
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.result-item,
.expected-item {
  transition: background-color 0.2s ease;
  &:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
}

.section-card {
  height: 100%;
}

.section-title {
  font-size: 1rem;
  font-weight: 500;
  padding: 12px 16px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 16px;
  font-size: 0.875rem;
}

.hold-image {
  background-color: rgba(0, 0, 0, 0.04);
}

.hold-actions,
.expected-actions {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
}

.history-line {
  white-space: nowrap;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>
